<script setup lang="ts">
import { computed, watch } from "vue";

import { EDurationUnit, TEDurationUnit } from "@/components/piloting/wizard/piloting-wizard.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { ETimeUnit } from "@/utils/time";

import Select from "primevue/select";

interface IProps {
  amountError?: string;
  unitError?: string;
}

const props = defineProps<IProps>();
const { t, k } = useTypedIntl();

const unit = defineModel<TEDurationUnit>("unit", {
  required: false,
  default: EDurationUnit.DAY,
});
const amount = defineModel<number>("amount", {
  required: false,
  default: 1,
});

const amountOptions = computed(() => {
  switch (unit.value) {
    case EDurationUnit.HOUR:
      return Array.from({ length: 24 }, (_, i) => ({
        value: i + 1,
        label: i + 1,
      }));
    case EDurationUnit.DAY:
      return Array.from({ length: 30 }, (_, i) => ({
        value: i + 1,
        label: i + 1,
      }));
    case EDurationUnit.WEEK:
      return Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: i + 1,
      }));
    case EDurationUnit.MONTH:
      return Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: i + 1,
      }));
    default:
      return [];
  }
});

const unitOptions = computed(() => {
  return [
    {
      label: t(k.global.units.temporal[ETimeUnit.HOUR]({ count: amount.value === 1 ? 0 : 1 })),
      value: ETimeUnit.HOUR,
    },
    {
      label: t(k.global.units.temporal[ETimeUnit.DAY]({ count: amount.value === 1 ? 0 : 1 })),
      value: ETimeUnit.DAY,
    },
    {
      label: t(k.global.units.temporal[ETimeUnit.WEEK]({ count: amount.value === 1 ? 0 : 1 })),
      value: ETimeUnit.WEEK,
    },
    {
      label: t(k.global.units.temporal[ETimeUnit.MONTH]({ count: amount.value === 1 ? 0 : 1 })),
      value: ETimeUnit.MONTH,
    },
  ];
});

watch(unit, () => {
  const foundAmount = amountOptions.value.find((k) => amount.value === k.value);
  if (foundAmount) {
    return;
  }

  amount.value = amountOptions.value[0].value;
});
</script>

<template>
  <div class="flex flex-row items-center justify-start gap-4">
    <div>
      <Select
        v-model="amount"
        data-test-id="duration-amount-select"
        option-label="label"
        option-value="value"
        :options="amountOptions"
      />
      <small>{{ props.amountError }}</small>
    </div>
    <div>
      <Select
        v-model="unit"
        data-test-id="duration-unit-select"
        option-label="label"
        option-value="value"
        :options="unitOptions"
      />
      <small>{{ props.unitError }}</small>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
